import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { exhibitorNav } from "./ExhibitorDashboard";
import QRScanner from "@/components/QRScanner";
import { Overline } from "@/components/Brand";
import { useEvent } from "@/context/EventContext";
import { toast } from "sonner";
import { BadgeCheck, CreditCard, UserCheck, Upload, Repeat, CalendarClock } from "lucide-react";

export default function ExhibitorScan() {
  const [tab, setTab] = useState("badge");
  const [result, setResult] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const cardRef = useRef();
  const navigate = useNavigate();
  const { effectivePhase } = useEvent();
  const isLive = effectivePhase === "live_event";

  const scanBadge = async (code) => {
    const badge = String(code).trim();
    try {
      const { data } = await api.post("/exhibitor/scan-badge", { badge_id: badge });
      setResult({ type: "badge", ...data });
      toast.success(`Logged visit: ${data.visitor.name}`);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const scanCard = async (file) => {
    if (!file) return;
    setCardBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/exhibitor/scan-card", fd);
      setResult({ type: "card", ...data });
      toast.success("Lead created from visiting card");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setCardBusy(false); if (cardRef.current) cardRef.current.value = ""; }
  };

  return (
    <Shell nav={exhibitorNav}>
      <Overline>Capture Leads</Overline>
      <h1 className="font-display text-3xl gold-text mt-2">Scan</h1>

      <div className="flex gap-2 mt-5">
        <button onClick={() => { setTab("badge"); setResult(null); }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition ${tab === "badge" ? "btn-gold" : "glass text-slate-300"}`} data-testid="scan-tab-badge"><BadgeCheck size={16} /> Visitor Badge</button>
        <button onClick={() => { setTab("card"); setResult(null); }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition ${tab === "card" ? "btn-gold" : "glass text-slate-300"}`} data-testid="scan-tab-card"><CreditCard size={16} /> Visiting Card</button>
      </div>

      {result ? (
        <div className="glass rounded-3xl p-6 mt-6 fade-up" data-testid="scan-result">
          <div className="h-14 w-14 rounded-full btn-gold flex items-center justify-center"><UserCheck size={26} className="text-[#0B0F12]" /></div>
          <h2 className="font-display text-2xl text-slate-100 mt-3">{result.lead?.name || result.visitor?.name}</h2>
          {result.type === "badge" ? (
            <>
              <p className="text-slate-400 text-sm capitalize">{result.visitor?.classification} · {result.visitor?.phone}</p>
              {result.lead?.visit_count > 1 && <p className="text-sky-300 text-sm mt-1 flex items-center gap-1"><Repeat size={13} /> Repeat visitor · {result.lead.visit_count} visits</p>}
            </>
          ) : (
            <div className="text-sm text-slate-400 mt-1 space-y-0.5">
              {result.extracted?.company && <p>{result.extracted.company}</p>}
              {result.extracted?.phone && <p className="font-mono-j">{result.extracted.phone}</p>}
              {result.extracted?.email && <p>{result.extracted.email}</p>}
            </div>
          )}
          <div className="flex gap-3 mt-5">
            <button onClick={() => navigate(`/exhibitor/leads/${result.lead.id}`)} className="flex-1 btn-gold py-3 rounded-xl" data-testid="scan-open-lead">Open Lead</button>
            <button onClick={() => setResult(null)} className="flex-1 glass py-3 rounded-xl text-slate-300" data-testid="scan-next">Scan Next</button>
          </div>
        </div>
      ) : tab === "badge" ? (
        isLive ? (
          <div className="glass rounded-3xl p-5 mt-6">
            <p className="text-slate-400 text-sm mb-4">Scan the visitor's JJS badge QR, or enter their badge/phone. Visit is logged with date & time and merged into one lead.</p>
            <QRScanner onResult={scanBadge} label="visitor badge" />
          </div>
        ) : (
          <div className="glass rounded-3xl p-6 mt-6 text-center" data-testid="badge-scan-disabled">
            <div className="h-14 w-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto"><CalendarClock size={26} className="text-amber-400" /></div>
            <h3 className="font-display text-xl text-slate-900 mt-4">Scanning opens 22–25 Dec</h3>
            <p className="text-slate-500 text-sm mt-2">Visitor badge check-ins are available during the live show. Until then, prepare your stall, catalogues and staff — and capture pre-show enquiries via visiting cards.</p>
          </div>
        )
      ) : (
        <div className="glass rounded-3xl p-6 mt-6 text-center">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto"><CreditCard size={26} className="text-amber-400" /></div>
          <p className="text-slate-400 text-sm mt-4">Upload or snap a visiting card. AI/OCR extracts the details into a new lead.</p>
          <input ref={cardRef} type="file" accept="image/*" capture="environment" onChange={(e) => scanCard(e.target.files[0])} className="hidden" data-testid="card-upload-input" />
          <button disabled={cardBusy} onClick={() => cardRef.current?.click()} className="w-full btn-gold py-3 rounded-xl mt-5 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="card-scan-button">
            <Upload size={16} /> {cardBusy ? "Extracting details…" : "Upload / Snap Card"}
          </button>
        </div>
      )}
    </Shell>
  );
}
